import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  ConnectionStatus,
  MicrophonePermission,
  TranscriptEntry,
  VapiEvent,
} from '../hooks/useVapi';

interface DebugPanelProps {
  status: ConnectionStatus;
  micPermission: MicrophonePermission;
  events: VapiEvent[];
  transcript: TranscriptEntry[];
  callId?: string | null;
  onClear?: () => void;
}

const statusIndicatorStyles: Record<ConnectionStatus, string> = {
  idle: 'bg-muted-foreground',
  connecting: 'bg-kaiva-warning',
  connected: 'bg-kaiva-success',
  disconnected: 'bg-border',
  error: 'bg-kaiva-coral',
};

function formatPayload(payload: unknown): string {
  if (payload === undefined) {
    return 'No payload';
  }

  if (typeof payload === 'string') {
    return payload;
  }

  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

function extractCallIdFromPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const data = payload as Record<string, unknown>;
  if (typeof data.callId === 'string' && data.callId.length > 0) {
    return data.callId;
  }

  const nestedCall = data.call;
  if (nestedCall && typeof nestedCall === 'object') {
    const nestedId = (nestedCall as Record<string, unknown>).id;
    if (typeof nestedId === 'string' && nestedId.length > 0) {
      return nestedId;
    }
  }

  return null;
}

export function DebugPanel({
  status,
  micPermission,
  events,
  transcript,
  callId,
  onClear,
}: DebugPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [eventOffset, setEventOffset] = useState(0);
  const [transcriptOffset, setTranscriptOffset] = useState(0);
  const eventsListRef = useRef<HTMLDivElement | null>(null);
  const transcriptListRef = useRef<HTMLDivElement | null>(null);

  const resolvedCallId = useMemo(() => {
    if (callId && callId.length > 0) {
      return callId;
    }

    for (let index = events.length - 1; index >= 0; index -= 1) {
      const extracted = extractCallIdFromPayload(events[index].payload);
      if (extracted) {
        return extracted;
      }
    }

    return null;
  }, [callId, events]);

  const visibleEvents = useMemo(
    () => events.slice(eventOffset),
    [eventOffset, events]
  );
  const visibleTranscript = useMemo(
    () => transcript.slice(transcriptOffset),
    [transcript, transcriptOffset]
  );

  useEffect(() => {
    if (!isOpen || !eventsListRef.current) {
      return;
    }

    const container = eventsListRef.current;
    container.scrollTop = container.scrollHeight;
  }, [isOpen, visibleEvents.length]);

  useEffect(() => {
    if (!isOpen || !transcriptListRef.current) {
      return;
    }

    const container = transcriptListRef.current;
    container.scrollTop = container.scrollHeight;
  }, [isOpen, visibleTranscript.length]);

  if (!import.meta.env.DEV) {
    return null;
  }

  const handleClear = () => {
    setEventOffset(events.length);
    setTranscriptOffset(transcript.length);
    onClear?.();
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        type="button"
        onClick={() => setIsOpen((previous) => !previous)}
        className="ml-auto block rounded-full border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground shadow-lg transition hover:bg-muted"
      >
        {isOpen ? 'Hide Debug' : 'Show Debug'}
      </button>

      {isOpen && (
        <section className="mt-2 w-[24rem] max-w-[90vw] rounded-xl border border-border bg-card text-foreground shadow-2xl">
          <header className="flex items-center justify-between border-b border-border px-3 py-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              VAPI Debug
            </h2>
            <button
              type="button"
              onClick={handleClear}
              className="rounded border border-border px-2 py-1 text-[11px] text-muted-foreground transition hover:border-kaiva-coral/40 hover:text-kaiva-coral"
            >
              Clear
            </button>
          </header>

          <div className="space-y-2 border-b border-border px-3 py-2 text-[11px]">
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${statusIndicatorStyles[status]}`}
              />
              <span className="text-muted-foreground">Status:</span>
              <span className="font-semibold text-foreground">{status}</span>
            </div>
            <div className="truncate">
              <span className="text-muted-foreground">Call ID: </span>
              <span className="text-foreground">{resolvedCallId ?? 'n/a'}</span>
            </div>
            <div className="truncate">
              <span className="text-muted-foreground">Mic: </span>
              <span className="text-foreground">{micPermission}</span>
            </div>
            <div className="text-muted-foreground">
              Events: {visibleEvents.length} | Transcript: {visibleTranscript.length}
            </div>
          </div>

          <div className="grid h-[26rem] grid-rows-2">
            <div className="flex min-h-0 flex-col border-b border-border">
              <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-kaiva-teal">
                Events
              </p>
              <div ref={eventsListRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 pb-3">
                {visibleEvents.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">No events yet.</p>
                ) : (
                  visibleEvents.map((event) => (
                    <details
                      key={event.id}
                      className="rounded border border-border bg-muted/60 p-2"
                    >
                      <summary className="cursor-pointer text-[11px] text-foreground">
                        [{new Date(event.timestamp).toLocaleTimeString()}] {event.type}
                      </summary>
                      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-[10px] leading-relaxed text-muted-foreground">
                        {formatPayload(event.payload)}
                      </pre>
                    </details>
                  ))
                )}
              </div>
            </div>

            <div className="flex min-h-0 flex-col">
              <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-kaiva-coral">
                Transcript
              </p>
              <div
                ref={transcriptListRef}
                className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 pb-3"
              >
                {visibleTranscript.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">No transcript yet.</p>
                ) : (
                  visibleTranscript.map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded border border-border bg-muted/60 p-2 text-[11px]"
                    >
                      <p className="text-muted-foreground">
                        {new Date(entry.timestamp).toLocaleTimeString()} | {entry.role} |{' '}
                        {entry.transcriptType}
                      </p>
                      <p className="mt-1 text-foreground">{entry.text}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default DebugPanel;
